import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Header } from '../components/layout/Header';
import { api } from '../services/api';
import type { CreateExpenseRequest } from '../types';

function formatMoney(amount: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
  }).format(amount);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-MX');
}

export function ExpensesPage() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<CreateExpenseRequest>({
    amount: 0,
    description: '',
    category_id: '',
  });

  const { data: expenses, isLoading } = useQuery({
    queryKey: ['expenses'],
    queryFn: () => api.getExpenses(50),
  });

  const { data: categories } = useQuery({
    queryKey: ['expenseCategories'],
    queryFn: () => api.getExpenseCategories(),
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateExpenseRequest) => api.createExpense(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setFormData({ amount: 0, description: '', category_id: '' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.amount > 0) {
      createMutation.mutate(formData);
    }
  };

  // Calculate summaries
  const today = new Date().toDateString();
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

  const todayTotal = expenses
    ?.filter((e) => new Date(e.expense_date).toDateString() === today)
    .reduce((sum, e) => sum + e.amount, 0) || 0;

  const weekTotal = expenses
    ?.filter((e) => new Date(e.expense_date) >= weekAgo)
    .reduce((sum, e) => sum + e.amount, 0) || 0;

  const monthTotal = expenses
    ?.filter((e) => new Date(e.expense_date) >= monthStart)
    .reduce((sum, e) => sum + e.amount, 0) || 0;

  return (
    <div>
      <Header title="Gastos" />

      {/* Quick Add Form */}
      <div className="card mb-6">
        <h2 className="text-lg font-semibold text-primary-500 mb-4">➕ Registrar Gasto</h2>
        <form onSubmit={handleSubmit} className="flex flex-wrap gap-3">
          <input
            type="number"
            className="input w-32"
            placeholder="Monto $"
            step="0.01"
            value={formData.amount || ''}
            onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
            required
          />
          <input
            type="text"
            className="input flex-1 min-w-48"
            placeholder="Descripción"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <select
            className="input w-48"
            value={formData.category_id}
            onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
          >
            <option value="">Sin categoría</option>
            {categories?.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? 'Guardando...' : 'Agregar'}
          </button>
        </form>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="card">
          <h2 className="text-lg font-semibold text-primary-500 mb-4">📊 Resumen</h2>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-primary-500">{formatMoney(todayTotal)}</div>
              <div className="text-sm text-gray-500">Hoy</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary-500">{formatMoney(weekTotal)}</div>
              <div className="text-sm text-gray-500">Semana</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-primary-500">{formatMoney(monthTotal)}</div>
              <div className="text-sm text-gray-500">Mes</div>
            </div>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-primary-500 mb-4">📁 Por Categoría</h2>
          <div className="space-y-2">
            {categories?.slice(0, 4).map((cat) => (
              <div key={cat.id} className="flex items-center gap-3 p-2 bg-dark-bg rounded-lg">
                <span>{cat.icon || '📁'}</span>
                <span className="flex-1">{cat.name}</span>
                <span className="text-sm text-gray-500">
                  {formatMoney(cat.monthly_budget || 0)} presupuesto
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Expenses Table */}
      <div className="card">
        <h2 className="text-lg font-semibold text-primary-500 mb-4">📝 Historial</h2>
        {isLoading ? (
          <p className="text-gray-500 text-center py-4">Cargando...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 border-b border-dark-border">
                  <th className="pb-3 font-medium">Fecha</th>
                  <th className="pb-3 font-medium">Descripción</th>
                  <th className="pb-3 font-medium">Categoría</th>
                  <th className="pb-3 font-medium text-right">Monto</th>
                </tr>
              </thead>
              <tbody>
                {expenses?.map((expense) => (
                  <tr key={expense.id} className="border-b border-dark-border/50 hover:bg-dark-border/30">
                    <td className="py-3 text-sm">{formatDate(expense.expense_date)}</td>
                    <td className="py-3">{expense.description || '—'}</td>
                    <td className="py-3">
                      {expense.category ? (
                        <span>
                          {expense.category.icon} {expense.category.name}
                        </span>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>
                    <td className="py-3 text-right text-primary-500 font-medium">
                      {formatMoney(expense.amount)}
                    </td>
                  </tr>
                ))}
                {!expenses?.length && (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-gray-500">
                      No hay gastos registrados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
